// src/features/dashboard/components/TeamLeaderDropdown.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ChevronDown,
  Search,
} from 'lucide-react';

import {
  getTeamActiveHoursTeamLeads,
  TeamLeader,
} from '../services/Manager/teamActiveHoursTeamLeadsApi';


// ============================================================================
// PROPS
// ============================================================================

interface Props {
  managerEmployeeCode: string;
  value: string;
  onChange: (
    employeeCode: string,
  ) => void;
}


// ============================================================================
// COMPONENT
// ============================================================================

const TeamLeaderDropdown = ({
  managerEmployeeCode,
  value,
  onChange,
}: Props) => {

  // ==========================================================================
  // STATE
  // ==========================================================================

  const [
    open,
    setOpen,
  ] = useState(false);

  const [
    search,
    setSearch,
  ] = useState('');

  const [
    selected,
    setSelected,
  ] = useState(
    'All Team Leaders',
  );

  const [
    teamMembers,
    setTeamMembers,
  ] = useState<
    TeamLeader[]
  >([]);

  const [
    loading,
    setLoading,
  ] = useState(false);


  // ==========================================================================
  // REF
  // ==========================================================================

  const dropdownRef =
    useRef<HTMLDivElement>(null);


  // ==========================================================================
  // LOAD TEAM LEADERS
  // ==========================================================================

  useEffect(() => {

    let isMounted = true;

    const loadTeamLeaders =
      async () => {

        try {

          setLoading(true);

          const response =
            await getTeamActiveHoursTeamLeads(
              managerEmployeeCode,
            );

          if (isMounted) {

            setTeamMembers(
              response ?? [],
            );

          }

        } catch (error) {

          console.error(
            'Team Active Hours Team Leads API Error:',
            error,
          );

          if (isMounted) {

            setTeamMembers([]);

          }

        } finally {

          if (isMounted) {

            setLoading(false);

          }

        }

      };

    loadTeamLeaders();

    return () => {

      isMounted = false;

    };

  }, [
    managerEmployeeCode,
  ]);


  // ==========================================================================
  // CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  // ==========================================================================

  useEffect(() => {

    const handleOutsideClick =
      (event: MouseEvent) => {

        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(
            event.target as Node,
          )
        ) {

          setOpen(false);

        }

      };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );

    return () => {

      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );

    };

  }, []);


  // ==========================================================================
  // UPDATE SELECTED TEAM LEADER
  // ==========================================================================

  useEffect(() => {

    if (!value) {

      setSelected(
        'All Team Leaders',
      );

      return;

    }

    const member =
      teamMembers.find(
        (item) =>
          String(
            item.employeeCode,
          ) ===
          String(value),
      );

    if (member) {

      setSelected(
        member.employeeName,
      );

    } else {

      setSelected(
        'All Team Leaders',
      );

    }

  }, [
    value,
    teamMembers,
  ]);


  // ==========================================================================
  // FILTER TEAM LEADERS
  // ==========================================================================

  const filteredLeaders =
    useMemo(() => {

      const normalizedSearch =
        search
          .trim()
          .toLowerCase();

      if (!normalizedSearch) {

        return teamMembers;

      }

      return teamMembers.filter(
        (member) =>
          member.employeeName
            ?.toLowerCase()
            .includes(
              normalizedSearch,
            ),
      );

    }, [
      teamMembers,
      search,
    ]);


  // ==========================================================================
  // SELECT TEAM LEADER
  // ==========================================================================

  const handleSelect = (
    employeeCode: string,
  ) => {

    onChange(
      employeeCode,
    );

    setSearch('');

    setOpen(false);

  };


  // ==========================================================================
  // SELECT ALL
  // ==========================================================================

  const handleSelectAll = () => {

    onChange('');

    setSelected(
      'All Team Leaders',
    );

    setSearch('');

    setOpen(false);

  };


  // ==========================================================================
  // TOGGLE DROPDOWN
  // ==========================================================================

  const handleToggle = () => {

    setOpen(
      (previous) =>
        !previous,
    );

  };


  // ==========================================================================
  // UI
  // ==========================================================================

  return (

    <div
      ref={dropdownRef}
      className="
        relative
        z-[100]
        w-full
      "
    >

      {/* ====================================================================
          SELECTED VALUE
      ===================================================================== */}

      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={
          handleToggle
        }
        className="
          relative
          z-[101]

          flex
          h-[38px]
          w-full
          items-center
          justify-between

          rounded-[10px]

          border
          border-[#D0D5DD]

          bg-white

          px-3

          text-left
          text-[11px]
          font-medium
          text-[#111827]

          outline-none

          transition

          hover:border-[#98A2B3]

          focus:border-[#84CAFF]
          focus:ring-1
          focus:ring-[#84CAFF]

          [@media(max-width:1100px)]:h-[34px]
          [@media(max-width:1100px)]:px-2
          [@media(max-width:1100px)]:text-[10px]

          [@media(max-width:1000px)]:h-[32px]
          [@media(max-width:1000px)]:px-1.5
          [@media(max-width:1000px)]:text-[9px]
        "
      >

        <span
          className="
            min-w-0
            flex-1
            truncate
          "
        >
          {selected}
        </span>

        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`
            ml-2
            shrink-0
            text-[#667085]
            transition-transform
            duration-150

            [@media(max-width:1000px)]:h-[12px]
            [@media(max-width:1000px)]:w-[12px]

            ${
              open
                ? 'rotate-180'
                : ''
            }
          `}
        />

      </button>


      {/* ====================================================================
          DROPDOWN PANEL
      ===================================================================== */}

      {open && (

        <div
          role="listbox"
          className="
            absolute
            left-0

            top-[42px]

            z-[9999]

            w-full
            min-w-full

            overflow-hidden

            rounded-[10px]

            border
            border-[#D0D5DD]

            bg-white

            shadow-[0_8px_24px_rgba(16,24,40,0.16)]

            [@media(max-width:1100px)]:top-[38px]

            [@media(max-width:1000px)]:top-[36px]
          "
          style={{
            backgroundColor:
              '#FFFFFF',

            isolation:
              'isolate',
          }}
        >

          {/* ================================================================
              SEARCH AREA
          ================================================================ */}

          <div
            className="
              bg-white
              p-2

              [@media(max-width:1000px)]:p-1.5
            "
          >

            <div
              className="
                relative
                bg-white
              "
            >

              <Search
                size={13}
                strokeWidth={2}
                className="
                  pointer-events-none

                  absolute
                  left-3
                  top-1/2

                  -translate-y-1/2

                  text-[#98A2B3]

                  [@media(max-width:1000px)]:left-2
                  [@media(max-width:1000px)]:h-[12px]
                  [@media(max-width:1000px)]:w-[12px]
                "
              />

              <input
                type="text"
                value={search}
                placeholder="Search team leader..."
                autoFocus

                onChange={(
                  event,
                ) => {

                  setSearch(
                    event.target.value,
                  );

                }}

                onClick={(
                  event,
                ) => {

                  event.stopPropagation();

                }}

                onKeyDown={(
                  event,
                ) => {

                  if (
                    event.key ===
                    'Escape'
                  ) {

                    setOpen(false);

                  }

                }}

                className="
                  h-[34px]
                  w-full

                  rounded-[8px]

                  border
                  border-[#84CAFF]

                  bg-white

                  pl-8
                  pr-2

                  text-[10px]
                  text-[#344054]

                  outline-none

                  placeholder:text-[#98A2B3]

                  focus:border-[#2563EB]

                  [@media(max-width:1100px)]:h-[30px]
                  [@media(max-width:1100px)]:px-2
                  [@media(max-width:1100px)]:pl-7
                  [@media(max-width:1100px)]:text-[10px]

                  [@media(max-width:1000px)]:h-[28px]
                  [@media(max-width:1000px)]:pl-6
                  [@media(max-width:1000px)]:text-[9px]
                "
              />

            </div>

          </div>


          {/* ================================================================
              DIVIDER
          ================================================================ */}

          <div
            className="
              h-px
              w-full
              bg-[#EAECF0]
            "
          />


          {/* ================================================================
              ALL TEAM LEADERS
          ================================================================ */}

          <div
            className="
              bg-white
              px-2
              py-1

              [@media(max-width:1000px)]:px-1.5
            "
          >

            <button
              type="button"
              role="option"
              aria-selected={
                value === ''
              }
              onClick={
                handleSelectAll
              }
              className={`
                block
                w-full

                rounded-[7px]

                bg-white

                px-3
                py-2

                text-left
                text-[10px]

                transition

                hover:bg-[#F5F9FF]

                [@media(max-width:1100px)]:px-2
                [@media(max-width:1100px)]:py-1.5

                [@media(max-width:1000px)]:px-1.5
                [@media(max-width:1000px)]:py-1

                ${
                  value === ''
                    ? 'font-semibold text-[#2563EB]'
                    : 'text-[#344054]'
                }
              `}
            >
              All Team Leaders
            </button>

          </div>


          {/* ================================================================
              TEAM LEADER LIST
          ================================================================ */}

          <div
            className="
              max-h-[170px]

              overflow-y-auto
              overflow-x-hidden

              bg-white

              pb-2

              [@media(max-width:1000px)]:max-h-[140px]
            "
          >

            {loading ? (

              <div
                className="
                  bg-white

                  px-3
                  py-3

                  text-[10px]
                  text-[#667085]

                  [@media(max-width:1000px)]:px-2
                  [@media(max-width:1000px)]:py-2
                  [@media(max-width:1000px)]:text-[9px]
                "
              >
                Loading team leaders...
              </div>

            ) : filteredLeaders.length ===
              0 ? (

              <div
                className="
                  bg-white

                  px-3
                  py-3

                  text-center
                  text-[10px]
                  text-[#667085]

                  [@media(max-width:1000px)]:px-2
                  [@media(max-width:1000px)]:py-2
                  [@media(max-width:1000px)]:text-[9px]
                "
              >
                No team leader found
              </div>

            ) : (

              filteredLeaders.map(
                (
                  member,
                ) => {

                  const isSelected =
                    String(
                      member.employeeCode,
                    ) ===
                    String(value);

                  return (

                    <button
                      key={
                        member.employeeCode
                      }

                      type="button"

                      role="option"

                      aria-selected={
                        isSelected
                      }

                      onClick={() =>
                        handleSelect(
                          member.employeeCode,
                        )
                      }

                      className={`
                        block
                        w-full

                        bg-white

                        px-3
                        py-2

                        text-left
                        text-[10px]

                        transition

                        hover:bg-[#F5F9FF]

                        [@media(max-width:1100px)]:px-2
                        [@media(max-width:1100px)]:py-1.5

                        [@media(max-width:1000px)]:px-1.5
                        [@media(max-width:1000px)]:py-1
                        [@media(max-width:1000px)]:text-[9px]

                        ${
                          isSelected
                            ? 'bg-[#EEF4FF] font-semibold text-[#2563EB]'
                            : 'text-[#111827]'
                        }
                      `}
                    >

                      <span
                        className="
                          block
                          truncate
                        "
                      >
                        {
                          member.employeeName
                        }
                      </span>

                    </button>

                  );

                },
              )

            )}

          </div>

        </div>

      )}

    </div>

  );
};


// ============================================================================
// EXPORT
// ============================================================================

export default TeamLeaderDropdown;